import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Newspaper, ArrowRight, Calendar, Tag } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

function ArticleCard({ a, idx }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.55, delay: (idx % 3) * 0.07 }}
            className="group flex flex-col overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium transition-all hover:-translate-y-1 hover:shadow-lg"
            data-testid={`actualite-card-${idx}`}
        >
            {a.cover_url ? (
                <Link to={`/actualite-pa/${a.slug}`} className="block aspect-[16/10] overflow-hidden">
                    <img src={a.cover_url} alt={a.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                </Link>
            ) : (
                <div className="flex aspect-[16/10] items-center justify-center bg-gradient-to-br from-brand-orange/10 to-brand-orange/5">
                    <Newspaper className="h-10 w-10 text-brand-orange/40" aria-hidden />
                </div>
            )}
            <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center gap-2 text-[11px] text-brand-smoke">
                    <Calendar className="h-3 w-3" /> {formatDate(a.published_at)}
                    {a.author && <span>· {a.author}</span>}
                </div>
                <h3 className="mt-3 font-display text-2xl leading-tight text-brand-ink">
                    <Link to={`/actualite-pa/${a.slug}`} className="hover:text-brand-orange">{a.title}</Link>
                </h3>
                {a.excerpt && <p className="mt-3 line-clamp-3 text-sm text-brand-ink/70">{a.excerpt}</p>}
                {a.tags?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                        {a.tags.slice(0, 3).map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 rounded-full border border-brand-orange/30 bg-brand-orange/5 px-2.5 py-0.5 text-[10px] font-medium text-brand-ink">
                                <Tag className="h-2.5 w-2.5 text-brand-orange" /> {t}
                            </span>
                        ))}
                    </div>
                )}
                <Link
                    to={`/actualite-pa/${a.slug}`}
                    className="mt-5 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-brand-orange hover:underline"
                    data-testid={`actualite-read-${idx}`}
                >
                    Lire l'article <ArrowRight className="h-3 w-3" />
                </Link>
            </div>
        </motion.article>
    );
}

export default function ActualitePAList() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        document.title = "L'actualité PA · Cursus Physio";
        const meta = document.querySelector('meta[name="description"]') || (() => {
            const m = document.createElement("meta");
            m.name = "description";
            document.head.appendChild(m);
            return m;
        })();
        meta.content = "L'actualité PA : études, événements, retours d'expérience et publications scientifiques autour de la Prévention Active en kinésithérapie.";
        api.get("/actualites")
            .then((r) => setArticles(r.data || []))
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <main className="bg-white" data-testid="page-actualite-pa">
            <section className="border-b border-brand-line bg-[#FAFAFA] px-6 py-20 sm:py-28">
                <div className="mx-auto max-w-7xl">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-orange/40 bg-brand-orange/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        <Newspaper className="h-3 w-3" /> Le fil de la Prévention Active
                    </span>
                    <h1 className="mt-6 font-display text-5xl leading-[0.95] text-brand-ink sm:text-6xl lg:text-7xl">
                        L'<em className="italic text-brand-smoke">actualité</em> PA.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base text-brand-ink/70 sm:text-lg">
                        Études, événements, retours d'expérience et publications scientifiques autour
                        de la Prévention Active en kinésithérapie.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
                {loading ? (
                    <div className="py-20 text-center text-sm text-brand-smoke">Chargement…</div>
                ) : err ? (
                    <div className="py-20 text-center text-sm text-red-600">{err}</div>
                ) : articles.length === 0 ? (
                    <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-brand-line bg-[#FAFAFA] px-8 py-20 text-center" data-testid="actualite-empty">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-orange/10">
                            <Newspaper className="h-8 w-8 text-brand-orange" />
                        </div>
                        <h2 className="mt-6 font-display text-2xl text-brand-ink">
                            Les premiers contenus arrivent prochainement.
                        </h2>
                        <p className="mt-3 text-sm text-brand-smoke">
                            Suivez-nous pour ne rien manquer.
                        </p>
                        <Link
                            to="/contact"
                            className="mt-7 inline-flex items-center gap-2 rounded-full border border-brand-ink/15 bg-white px-6 py-3 text-sm font-semibold text-brand-ink hover:border-brand-ink"
                            data-testid="actualite-empty-cta"
                        >
                            S'abonner aux notifications <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="actualites-grid">
                        {articles.map((a, i) => <ArticleCard key={a.id} a={a} idx={i} />)}
                    </div>
                )}
            </section>
        </main>
    );
}
